<template>
    <q-btn
        :outline="outline"
        :color="color"
        class="filter-panel text-capitalize bg-white text-weight-medium"
        :class="
            $q.screen.lt.sm
                ? 'filter-panel-mobile'
                : $q.screen.lt.md
                  ? 'filter-panel-tablet'
                  : ''
        "
        :icon="icon"
        :label="label"
        :align="alignLabel"
    >
        <q-menu :offset="[0, 10]">
            <div :style="searchOption ? 'width: 350px' : ''" class="q-pa-lg">
                <label class="text-subtitle1 text-weight-bold">
                    {{ dropdownMenuLabel ? dropdownMenuLabel : label }}
                </label>
                <!-- search option -->
                <q-input
                    v-if="searchOption"
                    ref="filterRef"
                    v-model="filter"
                    label="Search"
                    outlined
                    class="q-mt-sm"
                    dense
                    :style="{ maxWidth: '350px' }"
                    color="accent"
                >
                    <template v-slot:append>
                        <q-icon
                            v-if="filter !== ''"
                            name="clear"
                            class="cursor-pointer"
                            @click="resetFilter"
                        ></q-icon>
                    </template>
                </q-input>

                <q-tree
                    :nodes="options"
                    node-key="value"
                    tick-strategy="leaf"
                    v-model:ticked="activeOptions"
                    :filter="filter"
                    v-model:selected="currentSelection"
                    selected-color="black"
                    :duration="20"
                    icon="add"
                    class="q-my-sm"
                    color="accent"
                >
                    <template v-slot:default-header="prop">
                        <div
                            :style="
                                prop.node.children && prop.node.children.length
                                    ? 'font-weight: bold; font-size: 15px'
                                    : ''
                            "
                        >
                            {{ prop.node.label }}
                        </div>
                    </template>
                </q-tree>
                <!-- button to select all/clear all options -->
                <q-btn
                    flat
                    color="primary"
                    :label="$t('Select All')"
                    @click="selectAll"
                />
                <q-btn
                    flat
                    color="primary"
                    :label="$t('Clear')"
                    @click="clearAll"
                />
            </div>
        </q-menu>
    </q-btn>
</template>

<script>
import { debounce } from 'quasar';

export default {
    name: 'FilterPanel',
    props: {
        /**
         * Name to be displayed for the panel.
         */
        label: {
            type: String,
            required: false
        },
        /**
         * Options in the panel.
         * Items must be an object and have the following format: { label: ..., value: ... }
         */
        options: {
            type: Array,
            required: true
        },
        /**
         * Option to be selected
         */
        selectedOptions: {
            type: [Set],
            required: true
        },
        /**
         * Icon to be displayed in the panel.
         */
        icon: {
            type: String,
            required: false
        },
        /**
         * Enable/disable search option.
         */
        searchOption: {
            type: Boolean,
            required: false
        },
        /**
         * Toggle drop-down button outline
         */
        outline: {
            type: Boolean,
            required: false,
            default: true
        },
        /**
         * Label alignment. defaults to center
         */
        alignLabel: {
            type: String,
            required: false,
            default: 'center'
        },
        color: {
            type: String,
            required: false,
            default: 'accent'
        },
        dropdownMenuLabel: {
            type: String,
            required: false
        }
    },
    watch: {
        /**
         * Watch for changes in the current selected options to enable disable tick
         * @param {string} value - The current selected option.
         */
        currentSelection(value) {
            if (value) {
                if (this.activeOptions.includes(value)) {
                    var index = this.activeOptions.indexOf(value);
                    if (index !== -1) {
                        this.activeOptions.splice(index, 1);
                    }
                } else {
                    this.activeOptions.push(value);
                }
            }

            this.emitActiveOptions();
        },
        /**
         * Watch for changes in the active selected options.
         */
        selectedOptions(newValue) {
            if (newValue.size === 0) {
                this.activeOptions = [];
            } else {
                this.activeOptions = Array.from(newValue);
            }
        },
        /**
         * Watch for changes in the filter.
         */
        activeOptions(newValue) {
            if (this.selectedOptions.size !== newValue.length) {
                this.emitActiveOptions();
            }
        }
    },
    emits: {
        toggle: value => {
            return typeof value === 'string' || typeof value === 'object';
        }
    },
    created() {
        // initialize options
        for (const option of this.selectedOptions) {
            this.activeOptions.push(option);
        }

        this.activeOptions = Array.from(this.activeOptions);

        // emit initial active options
        // set debounce on emitting options
        this.emitActiveOptions = debounce(this.emitActiveOptions, 250);
    },
    data() {
        return {
            activeOptions: [],
            showFilter: false,
            filter: '',
            filterRef: {},
            currentSelection: ''
        };
    },
    methods: {
        /**
         * Emit active options.
         */
        emitActiveOptions() {
            let emittableOptions = this.activeOptions;

            if (this.activeOptions.length === 0) {
                emittableOptions = new Set();
            } else {
                emittableOptions = new Set(this.activeOptions);
            }

            // convert options to emit as a set
            this.$emit('toggle', emittableOptions);
        },
        /**
         * Reset the filter (search)
         */
        resetFilter() {
            this.filter = '';
            this.filterRef = {};
        },
        /**
         * Select all options.
         */
        selectAll() {
            // if options have children, then select all children else select all
            this.options.forEach(option => {
                if (option.children) {
                    option.children.forEach(child => {
                        if (!this.activeOptions.includes(child.value)) {
                            this.activeOptions.push(child.value);
                        }
                    });
                } else {
                    if (!this.activeOptions.includes(option.value)) {
                        this.activeOptions.push(option.value);
                    }
                }
            });
            this.emitActiveOptions();
        },
        /**
         * Clear all options.
         */
        clearAll() {
            this.activeOptions = [];
            this.emitActiveOptions();
        }
    },
    mounted() {
        this.$q.iconSet.arrow.dropdown = '';
    }
};
</script>

<style lang="scss" scoped></style>
